import { Injectable } from '@angular/core';

import { FruHttpService } from './fru-http.service';
import { JornayaService } from './jornaya.service';

export interface JornayaNotification {
  type: string;
  message: string;
}

export interface JornayaTokenResult {
  token: string | null;
  status?: string;
  notification?: JornayaNotification;
}

@Injectable({
  providedIn: 'root'
})
export class JornayaFormService {
  private messages = {
    script_failed_to_load: 'We weren\'t able to load a required verification step. This is usually caused by an ad blocker or browser privacy extension. Please disable any ad blockers for this site, refresh the page, and try again. If you\'re not using an ad blocker, try a different browser or device.',
    script_not_loaded: 'A required verification step is still loading. Please wait a moment and try again. If this keeps happening, try disabling ad blockers for this site, refreshing the page, or using a different browser or device.',
    script_loaded_token_missing: 'A required verification step did not complete. Please refresh the page and try again. If this keeps happening, try a different browser or device.',
    token_input_missing: 'Something went wrong on our end while preparing the form. Please refresh the page and try again.',
    token_timeout: 'A required verification step took too long to complete. Please refresh the page and try again. If this keeps happening, try disabling ad blockers for this site or using a different browser or device.',
    default: 'We weren\'t able to complete a required verification step. This is usually caused by an ad blocker or browser privacy extension. To submit this form, please disable any ad blockers for this site, then refresh the page and try again. If you\'re not using an ad blocker, try a different browser or device.',
  };

  constructor(
    private fruHttp: FruHttpService,
    private jornayaService: JornayaService,
  ) { }

  async waitForTokenOrReport(encryptedLeadId: string): Promise<JornayaTokenResult> {
    const token = await this.jornayaService.waitForToken();

    if (token) {
      return { token };
    }

    return this.reportMissingToken(encryptedLeadId);
  }

  getTokenOrReport(encryptedLeadId: string): JornayaTokenResult {
    const token = this.jornayaService.getToken();

    if (token) {
      return { token };
    }

    return this.reportMissingToken(encryptedLeadId);
  }

  private reportMissingToken(encryptedLeadId: string): JornayaTokenResult {
    const status = this.jornayaService.getTokenStatus();

    if (encryptedLeadId) {
      // fire-and-forget; callers show the user-facing notification from the return value
      this.fruHttp.jornayaNotLoaded(encryptedLeadId, status).subscribe({ error: () => {} });
    }

    return {
      token: null,
      status,
      notification: {
        type: 'warning',
        message: this.messages[status] || this.messages.default,
      },
    };
  }
}
